import { UIEvent, useCallback, useEffect, useRef, useState } from 'react';

import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, IconButton, Link, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import {
  MRT_ColumnDef,
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_ColumnOrderState,
  MRT_ColumnSizingState,
  MRT_FilterFns,
  MRT_GroupingState,
  MRT_RowVirtualizer,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableState,
  MRT_VisibilityState,
  useMaterialReactTable,
} from 'material-react-table';
import { useInfiniteQuery, useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CardDemonstration } from 'src/components/CardDemonstration';
import { FavoriteRowButton } from 'src/components/FavoriteRowButton';
import MaterialTable from 'src/components/MaterialTable';
import { MaterialTableDefaultProps } from 'src/components/MaterialTable/MaterialTableDefaultProps';
import ModalButton from 'src/components/OnboardingModal/ModalButton';
import ToolbarInternal from 'src/pages/AllOperations/ToolbarInternal';
import { findFavoriteByOperationMarket } from 'src/services/favorite';
import { getPayWallBanner } from 'src/services/informative';
import { getColumnsBasicView } from 'src/services/operation';
import { getDebenturesOperationsList } from 'src/services/operation/operationDebentures';
import themev5 from 'src/themev5';
import { StrapiResponse } from 'src/types/http';
import { DebentureOperationListItem } from 'src/types/operations/debentures';

import { QueryParams } from '..';
import { DataExport } from '../DataExport';
import SaveFilterModal from '../SaveFilterModal';
import ShareButton from '../Share';
import { useCsvExport } from '../hooks/useCsvExport';
import { useRestoreSharedTableState } from '../hooks/useRestoreSharedTableState';
import { buildFilters, COLUMN_VISIBILITY, validateFilters } from '../utils';
import {
  buildShareUrl,
  getRestorableColumnSizing,
  hydrateSharedDateFilters,
  isColumnSizingState,
  mergeRestorableColumnSizing,
} from '../utils/shareUtils';
import {
  buildDefaultColumn,
  getLatestUpdatedAt,
  parseDateValue,
  populateFavorite,
} from '../utils/tableHelpers';

const PAGE_SIZE = 500;

type DebenturesTableProps = {
  query: QueryParams;
  setQuery: (p: any) => void;
  hasPermission: boolean;
  isAuthLoading: boolean;
  handlePrint: () => void;
  handleModalOpen: () => void;
};

function getDebenturesLink(row: DebentureOperationListItem) {
  return `/operacao/debentures/${row.slug}/${row.hashId}`;
}

export default function DebenturesTable({
  query,
  setQuery,
  hasPermission,
  isAuthLoading,
  handlePrint,
  handleModalOpen,
}: DebenturesTableProps) {
  const [columns, setColumns] = useState<MRT_ColumnDef<any>[]>([]);
  const [grouping, setGrouping] = useState<MRT_GroupingState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {},
  );
  const [columnFilterFns, setColumnFilterFns] =
    useState<MRT_ColumnFilterFnsState>({});
  const [defaultTableState] = useState<Partial<MRT_TableState<any>>>({
    grouping: [],
    columnFilterFns: {},
    columnFilters: [],
    sorting: [],
    columnOrder: [],
    columnSizing: {},
    columnVisibility: COLUMN_VISIBILITY[query.market],
  });
  const [operationsData, setOperationsData] = useState<
    DebentureOperationListItem[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [columnTypes, setColumnTypes] = useState<Record<string, string>>();
  const [selectedFilter, setSelectedFilter] = useState<any>();
  const [openSaveFilterModal, setOpenSaveFilterModal] =
    useState<boolean>(false);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const previousScrollTopRef = useRef<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    startCsvExport,
    progress: csvProgress,
    downloadUrl,
    status,
    isExporting,
    resetExport,
    downloadCurrentCsv,
  } = useCsvExport(query.market);

  const handleAddFavorite = (rowId: number, favorite: any) => {
    setOperationsData((prev: any) =>
      prev.map((item: any) =>
        item.id === rowId ? { ...item, favorite } : item,
      ),
    );
  };

  const handleRemoveFavorite = (rowId: number) => {
    setOperationsData((prev: any) =>
      prev.map((item: any) =>
        item.id === rowId ? { ...item, favorite: undefined } : item,
      ),
    );
  };

  const injectState = (newState: Partial<MRT_TableState<any>>) => {
    const allColumns = _.keys(columnTypes);

    if (!_.isEmpty(newState?.columnVisibility)) {
      setColumnVisibility((prev) =>
        allColumns.reduce(
          (acc, cur) =>
            ({
              ...acc,
              [cur]: newState.columnVisibility?.[cur],
            }) as MRT_VisibilityState,
          prev,
        ),
      );
    }
    if (!_.isEmpty(newState?.columnFilterFns)) {
      setColumnFilterFns((prev) =>
        _.merge(prev, _.pick(newState.columnFilterFns, _.keys(prev))),
      );
    }
    if (
      _.isArray(newState?.columnFilters) &&
      !_.isEmpty(newState?.columnFilters)
    ) {
      setColumnFilters(
        hydrateSharedDateFilters(
          newState.columnFilters.filter((f) => allColumns.includes(f.id)),
          columnTypes,
        ),
      );
    }
    if (_.isArray(newState?.sorting) && !_.isEmpty(newState?.sorting)) {
      setSorting(newState.sorting.filter((s) => allColumns.includes(s.id)));
    }
    if (_.isArray(newState?.columnOrder) && !_.isEmpty(newState?.columnOrder)) {
      setColumnOrder([
        ...new Set([
          ...newState.columnOrder,
          ...allColumns.filter((c) => !newState.columnOrder?.includes(c)),
        ]),
      ]);
    }
    if (_.isArray(newState?.grouping) && !_.isEmpty(newState?.grouping)) {
      setGrouping(allColumns.filter((c) => newState.grouping?.includes(c)));
    }
    if (
      isColumnSizingState(newState?.columnSizing) &&
      !_.isEmpty(newState?.columnSizing)
    ) {
      setColumnSizing((prev) =>
        mergeRestorableColumnSizing(
          prev,
          getRestorableColumnSizing(newState.columnSizing, allColumns),
        ),
      );
    }
  };

  const isDisabledSaveFilter = (table: MRT_TableInstance<any>): boolean =>
    table.getState().isSaving ||
    _.isEqual(
      _.pick(table.getState(), _.keys(defaultTableState)),
      defaultTableState,
    );

  const { isLoading: isLoadingColumnsData, refetch: refetchColumns } = useQuery(
    {
      queryKey: ['operation-columns-debentures', query.market, query.mode],
      queryFn: () =>
        getColumnsBasicView({ market: query.market.toLowerCase() }),
      onSuccess: ({ data }: { data: MRT_ColumnDef<any>[] }) => {
        const processedColumns: MRT_ColumnDef<any>[] = [
          {
            accessorKey: 'favorite',
            accessorFn: (row) => row?.favorite?.operation,
            header: '',
            Header: function ({ table }) {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center">
                  <IconButton
                    onClick={() => setOpenSaveFilterModal(true)}
                    disabled={isDisabledSaveFilter(table)}
                    sx={{
                      '& > svg': { color: '#FF8211' },
                      '&:disabled': { '& > svg': { color: '#BDBDBD' } },
                    }}>
                    <SaveAltIcon sx={{ fontSize: '26px' }} />
                  </IconButton>
                  <Typography fontSize={12} textAlign="center">
                    Salvar
                  </Typography>
                </Box>
              );
            },
            muiTableHeadCellProps: () => ({
              sx: { alignItems: 'center', justifyContent: 'center' },
            }),
            maxSize: 75,
            columnDefType: 'display',
            Cell: ({ cell }: { cell: any }) => (
              <FavoriteRowButton
                type="operation"
                rowId={cell.row.original?.id}
                favorite={cell.row.original?.favorite}
                handleAddFavorite={handleAddFavorite}
                handleRemoveFavorite={handleRemoveFavorite}
              />
            ),
          },
          ...data.map((initialColumn): MRT_ColumnDef<any> => {
            switch (initialColumn.accessorKey) {
              case 'nomeOperacao':
                return {
                  ...initialColumn,
                  Cell: ({ cell }) => (
                    <Link
                      component={RouterLink}
                      to={getDebenturesLink(cell.row.original)}>
                      {(cell.getValue() || '') as string}
                    </Link>
                  ),
                };
              default:
                return buildDefaultColumn(initialColumn);
            }
          }),
        ];

        setColumns(processedColumns);
        setColumnVisibility(COLUMN_VISIBILITY[query.market]);
        setColumnFilterFns(
          data.reduce(
            (acc: MRT_ColumnFilterFnsState, item: any) => ({
              ...acc,
              [item.accessorKey as string]: item._filterFn ?? item.filterFn,
            }),
            {},
          ),
        );
        setColumnTypes(
          data.reduce(
            (acc, cur) => ({
              ...acc,
              [String(cur.accessorKey)]: String(cur.meta),
            }),
            {} as Record<string, string>,
          ),
        );
      },
      enabled: hasPermission,
    },
  );

  const {
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingOperations,
    isFetching,
    refetch: refetchData,
  } = useInfiniteQuery({
    queryKey: [
      'operation-data-basic-debentures',
      query.market,
      query.mode,
      columnFilters,
      columnFilterFns,
      globalFilter,
    ],
    queryFn: ({ pageParam = 1 }) =>
      getDebenturesOperationsList({
        page: pageParam,
        pageSize: PAGE_SIZE,
        filters: buildFilters(
          columnFilters,
          columnFilterFns,
          columns,
          globalFilter,
        ),
      }),
    getNextPageParam: (lastPage) =>
      lastPage?.meta?.pagination?.page < lastPage?.meta?.pagination?.pageCount
        ? lastPage.meta.pagination.page + 1
        : undefined,
    enabled:
      hasPermission &&
      !!query.market &&
      !!query.mode &&
      !isLoadingColumnsData &&
      !validateFilters(columnFilters, columns, columnTypes ?? {}),
    onSuccess: (responsePages) => {
      const allOperations =
        responsePages.pages.flatMap(
          (page: StrapiResponse<DebentureOperationListItem[]>) =>
            page.data ?? [],
        ) ?? [];
      if (favorites?.data?.length) {
        setOperationsData(populateFavorite(allOperations, favorites.data));
      } else {
        setOperationsData(allOperations);
      }
      const firstPageMeta = responsePages.pages[0]?.meta as
        | StrapiResponse<DebentureOperationListItem[]>['meta']
        | undefined;
      setTotalCount(firstPageMeta?.pagination?.total ?? allOperations.length);
    },
    onError: () => {
      setTotalCount(0);
      setOperationsData([]);
    },
  });

  const {
    data: favorites,
    isLoading: isLoadingFavorites,
    refetch: refetchFavorites,
  } = useQuery(
    ['favorite-operation', query.market],
    () => findFavoriteByOperationMarket(query.market.toUpperCase()),
    {
      enabled: !isLoadingOperations && !isLoadingColumnsData,
      onSuccess: async () => {
        await refetchData();
      },
    },
  );

  const { data: contentPayWallBanner } = useQuery(
    'paywall',
    () => getPayWallBanner('operationBasicView'),
    { enabled: !hasPermission && !isAuthLoading },
  );

  const totalFetched = operationsData.length;

  const latestUpdatedAt = getLatestUpdatedAt(operationsData);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (
        !containerRefElement ||
        !hasNextPage ||
        isFetching ||
        totalFetched >= totalCount
      ) {
        return;
      }
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetching, totalFetched, totalCount],
  );

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const { scrollTop } = target;
    if (scrollTop !== previousScrollTopRef.current) {
      previousScrollTopRef.current = scrollTop;
      fetchMoreOnBottomReached(target);
    }
  };

  function parseValue(id: string, value: any) {
    if (!value) return value;
    switch (columnTypes?.[id]) {
      case 'float':
      case 'decimal':
        return parseFloat(
          value.toString().trim().replaceAll('.', '').replaceAll(',', '.'),
        );
      case 'date':
      case 'datetime':
        return parseDateValue(value) ?? value;
    }
    return value;
  }

  function normalizeFilteredValue(filter: any) {
    return (row: any, id: string, filterValue: string | number, ...args: any) =>
      filter(row, id, parseValue(id, filterValue), ...args);
  }

  const normalizeFilteredRangeValue =
    (filter: any) =>
    (
      row: any,
      id: string,
      filterValue: [string | number, string | number],
      ...args: any
    ) =>
      filter(
        row,
        id,
        filterValue.map((v) => parseValue(id, v)),
        ...args,
      );

  const resetTableToDefault = () => {
    setSelectedFilter(null);
    tableInstance.resetColumnFilters();
    tableInstance.resetColumnOrder();
    tableInstance.resetColumnPinning();
    tableInstance.resetColumnSizing();
    tableInstance.resetColumnVisibility();
    tableInstance.resetGlobalFilter();
    tableInstance.resetGrouping();
    tableInstance.resetSorting();
    tableInstance.resetHeaderSizeInfo();
    injectState(defaultTableState);
  };

  const handleSelectFilter = (values: any) => {
    resetTableToDefault();
    setSelectedFilter(values);
    injectState(values?.data);
  };

  const handleTableChange = (values: any, callback: (values: any) => void) => {
    setSelectedFilter(null);
    callback(values);
  };

  const fakeColumns: MRT_ColumnDef<any>[] = Array(4)
    .fill({})
    .map((_, index) => ({
      accessorKey: `debentures-column${index + 1}`,
      header: `Coluna${index + 1}`,
    }));

  const fakeDataRow = fakeColumns.reduce((acc, column, index) => {
    return {
      ...acc,
      [column.accessorKey as string]: `Dado ${index + 1}`,
    };
  }, {});

  const fakeRows = Array(10).fill(fakeDataRow);

  const tableInstance = useMaterialReactTable({
    ...MaterialTableDefaultProps,
    filterFns: {
      ...MRT_FilterFns,
      between: normalizeFilteredRangeValue(MRT_FilterFns.between),
      betweenInclusive: normalizeFilteredRangeValue(
        MRT_FilterFns.betweenInclusive,
      ),
      fuzzy: normalizeFilteredValue(MRT_FilterFns.fuzzy),
      contains: normalizeFilteredValue(MRT_FilterFns.contains),
      empty: normalizeFilteredValue(MRT_FilterFns.empty),
      endsWith: normalizeFilteredValue(MRT_FilterFns.endsWith),
      equals: normalizeFilteredValue(MRT_FilterFns.equals),
      greaterThan: normalizeFilteredValue(MRT_FilterFns.greaterThan),
      greaterThanOrEqualTo: normalizeFilteredValue(
        MRT_FilterFns.greaterThanOrEqualTo,
      ),
      lessThan: normalizeFilteredValue(MRT_FilterFns.lessThan),
      lessThanOrEqualTo: normalizeFilteredValue(
        MRT_FilterFns.lessThanOrEqualTo,
      ),
      notEmpty: normalizeFilteredValue(MRT_FilterFns.notEmpty),
      notEquals: normalizeFilteredValue(MRT_FilterFns.notEquals),
      startsWith: normalizeFilteredValue(MRT_FilterFns.startsWith),
    },
    data: hasPermission ? operationsData : fakeRows,
    columns: hasPermission ? columns : fakeColumns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    enablePagination: false,
    enableFilters: hasPermission,
    enableColumnFilters: hasPermission,
    enableGlobalFilter: true,
    enableColumnResizing: hasPermission,
    manualFiltering: true,
    enableHiding: hasPermission,
    enableColumnOrdering: hasPermission,
    rowCount: totalCount,
    positionGlobalFilter: 'right',
    muiTableContainerProps: {
      onScroll: handleScroll,
      sx: (theme) => ({
        minHeight: '300px',
        maxHeight: '600px',
        border: '1px solid #E0E0E0',
        WebkitFilter: hasPermission ? 'unset' : 'blur(5px)',
        '&::-webkit-scrollbar': {
          height: 10,
          width: 10,
          WebkitAppearance: 'none',
          border: '1px solid #E0E0E0',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 5,
          backgroundColor: theme.palette.grey['800'],
        },
        userSelect: 'none',
        WebkitUserSelect: 'none',
        KhtmlUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        'o-user-select': 'none',
        '&::selection, &::-moz-selection': { background: 'none' },
      }),
    },
    muiBottomToolbarProps: {
      sx: {
        border: '1px solid #E0E0E0',
        borderTop: 'none',
        minHeight: 'unset',
      },
    },
    muiSearchTextFieldProps: {
      sx: {
        border: 'none',
        padding: '8px',
        '& .MuiInputBase-root': { background: 'transparent' },
        '& .MuiInputBase-input': {
          '&::placeholder': {
            fontSize: '14px',
            color: '#6D6E71',
            fontWeight: 500,
          },
          '&:focus': { borderBottom: '2px solid #FF8211' },
          borderBottom: '1px solid #E4E5E9',
          fontSize: '14px',
          padding: '0 4px',
        },
        '.MuiSvgIcon-root': { color: '#FF8211', width: '20px', height: '20px' },
        '.MuiInputAdornment-root': { display: 'none' },
      },
      placeholder: 'Buscar',
      variant: 'standard',
    },
    muiTablePaperProps: ({ table }) => ({
      elevation: 0,
      style: {
        zIndex: table.getState().isFullScreen ? 1201 : undefined,
      },
    }),
    muiTopToolbarProps: ({ table }) => ({
      'aria-disabled': !hasPermission,
      sx: {
        padding: '0 16px',
        backgroundColor: '#FAFAFA',
        boxShadow: '0px 5px 6px 0px rgba(229, 229, 229, 0.50)',
        borderRadius: '3px 3px 0px 0px',
        borderTop: '1px solid #E0E0E0',
        borderRight: '1px solid #E0E0E0',
        borderLeft: '1px solid #E0E0E0',
        '& > .MuiBox-root': {
          flexDirection: 'row-reverse',
          justifyContent: table.getState().isFullScreen
            ? 'flex-end'
            : 'justify-content',
          alignItems: 'center',
          '& .MuiCollapse-root:first-child': { display: 'none' },
        },
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <ToolbarInternal
        table={table}
        market={query.market?.toUpperCase()}
        indicator={undefined}
        selectedFilter={selectedFilter}
        handleSelectFilter={handleSelectFilter}
        handleRemoveSelectedFilter={resetTableToDefault}
        type={'basic'}
        disabled={!hasPermission}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <Box display="flex" flexDirection="column" width="100%">
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            p: '4px',
            width: '100%',
            justifyContent: totalCount ? 'space-between' : 'flex-end',
          }}>
          {totalCount > 0 && (
            <Typography sx={{ fontSize: 12, color: '#4F4F4F' }}>
              {`Exibindo ${totalFetched} de ${totalCount}`}
            </Typography>
          )}
          {latestUpdatedAt && (
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {`Atualizado em ${format(parseISO(latestUpdatedAt), 'dd/MM/yyyy')}`}
            </Typography>
          )}
        </Box>
      </Box>
    ),
    onColumnFiltersChange: (updater) =>
      handleTableChange(updater, setColumnFilters),
    onColumnFilterFnsChange: (values) =>
      handleTableChange(values, setColumnFilterFns),
    onGlobalFilterChange: (value) => handleTableChange(value, setGlobalFilter),
    onGroupingChange: setGrouping,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    initialState: defaultTableState,
    state: {
      isLoading: hasPermission ? isLoading : false,
      showLoadingOverlay: hasPermission ? isLoading || isFetching : false,
      showProgressBars: hasPermission ? isFetching : false,
      density: 'compact',
      columnFilters,
      columnFilterFns,
      globalFilter,
      grouping,
      sorting,
      columnOrder,
      columnSizing,
      columnVisibility,
      showColumnFilters: hasPermission,
      showGlobalFilter: hasPermission,
      showAlertBanner: !!validateFilters(
        columnFilters,
        columns,
        columnTypes ?? {},
      ),
    },
  });

  const handleExportRows = async () => {
    try {
      const filters = buildFilters(
        columnFilters,
        columnFilterFns,
        columns,
        globalFilter,
      );

      const convertedSorting = sorting.map((item) => ({
        [item.id]: item.desc ? 'desc' : 'asc',
      }));

      await startCsvExport({
        market: query.market.toLowerCase(),
        permission: 'operacoes_exportar',
        filters,
        mode: 'operation',
        sorting: convertedSorting,
      });
    } catch {
      toast.warn('Erro ao exportar dados. Tente novamente.', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        progress: undefined,
        style: {
          backgroundColor: 'white',
          color: themev5.palette.primary.main,
        },
      });
    }
  };

  useEffect(() => {
    setIsLoading(
      isAuthLoading ||
        isLoadingColumnsData ||
        isLoadingOperations ||
        isLoadingFavorites ||
        !columns?.length,
    );
    if (!hasPermission && !isAuthLoading) {
      setIsLoading(false);
    }
  }, [
    isLoadingColumnsData,
    isLoadingOperations,
    isLoadingFavorites,
    columns,
    hasPermission,
    isAuthLoading,
  ]);

  useRestoreSharedTableState({
    shared: query.shared,
    stateKey: query.market,
    isLoading: isAuthLoading || isLoadingColumnsData || !columns?.length,
    hasPermission,
    hasColumns: Boolean(columnTypes && Object.keys(columnTypes).length),
    injectState: (nextState) =>
      injectState(nextState as Partial<MRT_TableState<any>>),
    clearShared: () => setQuery({ ...query, shared: null }),
  });

  useEffect(() => {
    if (hasPermission) {
      resetTableToDefault();

      if (typeof window !== 'undefined') {
        setOperationsData([]);
        void refetchColumns();
        void refetchFavorites();
      }
    }
  }, [query.market, hasPermission]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box>
        <Box
          p={2}
          pr={3}
          display="flex"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box>
            <ModalButton onClick={handleModalOpen} disabled={!hasPermission} />
          </Box>
          <DataExport
            handleExport={handleExportRows}
            handlePrint={handlePrint}
            permission={'operacoes_exportar'}
            hasData={!!operationsData?.length}
            downloadUrl={downloadUrl}
            progress={csvProgress}
            isExporting={isExporting}
            status={status}
            onReset={resetExport}
            onDownload={downloadCurrentCsv}
          />
          <ShareButton
            getUrlToShare={() =>
              buildShareUrl({
                market: query.market,
                sorting,
                columnFilterFns,
                columnFilters,
                columnSizing,
                columnVisibility,
                columnOrder,
                grouping,
              })
            }
            disabled={!hasPermission}
          />
        </Box>
        <MaterialTable table={tableInstance} />
      </Box>
      {!hasPermission && contentPayWallBanner?.operationBasicView && (
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            top: '40%',
            left: 0,
            px: '10px',
          }}>
          <CardDemonstration
            title={contentPayWallBanner?.operationBasicView?.title}
            subTitle={contentPayWallBanner?.operationBasicView?.subTitle}
            labelButton={contentPayWallBanner?.operationBasicView?.labelButton}
            url={contentPayWallBanner?.operationBasicView?.url}
          />
        </Box>
      )}
      <SaveFilterModal
        isOpen={openSaveFilterModal}
        handleClose={() => setOpenSaveFilterModal(false)}
        filterData={{
          type: query.mode,
          indicator: query.indicator,
          market: query.market,
          data: {
            grouping,
            columnFilterFns,
            columnFilters,
            sorting,
            columnOrder,
            columnSizing,
            columnVisibility,
          },
        }}
      />
    </Box>
  );
}
